import type { AccountRow, UsageTotals } from '@/api/client';
import { fmtMinutes, fmtNum, fmtUsd } from '@/lib/format';
import { Card, labelText } from '@/ui';

function Stat({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <Card className="px-5 py-4">
      <div className={labelText}>{label}</div>
      <div className="mt-2 text-[28px] leading-none font-semibold tracking-[-0.02em] tabular-nums text-neutral-950">
        {value}
      </div>
      <div className="mt-2 text-[12px] text-neutral-400">{note ?? '\u00a0'}</div>
    </Card>
  );
}

export function SummaryCards({
  accounts,
  totals,
  variant = 'messaging',
}: {
  accounts: AccountRow[] | null;
  totals: UsageTotals | null;
  variant?: 'messaging' | 'upsells';
}) {
  const live = accounts?.filter((a) => a.bffLive).length ?? null;
  const livePct = accounts?.length && live != null ? Math.round((live / accounts.length) * 100) : 0;
  const dash = (v: string) => (accounts && totals ? v : '-');

  if (variant === 'upsells') {
    const locks = accounts?.filter((a) => (a.numLiveLocks ?? 0) > 0).length ?? null;
    const conversion = totals && totals.offersSent > 0 ? (totals.upsellsSold / totals.offersSent) * 100 : null;
    return (
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Stat
          label="Live on inbox"
          value={accounts ? `${fmtNum(live)} / ${fmtNum(accounts.length)}` : '-'}
          note={accounts ? `${livePct}% of locations` : undefined}
        />
        <Stat
          label="Locks connected"
          value={accounts ? `${fmtNum(locks)} / ${fmtNum(accounts.length)}` : '-'}
          note={
            accounts ? `${fmtNum(accounts.reduce((sum, a) => sum + (a.numLiveLocks ?? 0), 0))} live locks` : undefined
          }
        />
        <Stat
          label="Upsell revenue"
          value={totals ? fmtUsd(totals.upsellRevenueUsdCents) : '-'}
          note="Selected date range"
        />
        <Stat label="Upsells sold" value={dash(fmtNum(totals?.upsellsSold))} note="All upsell types" />
        <Stat
          label="Offers sent"
          value={dash(fmtNum(totals?.offersSent))}
          note={conversion == null ? 'No offers yet' : `${conversion.toFixed(1)}% converted`}
        />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
      <Stat
        label="Locations live"
        value={accounts ? `${fmtNum(live)} / ${fmtNum(accounts.length)}` : '-'}
        note={accounts ? `${livePct}% of accounts created` : undefined}
      />
      <Stat
        label="Upsell revenue"
        value={totals ? fmtUsd(totals.upsellRevenueUsdCents) : '-'}
        note="Selected date range"
      />
      <Stat label="Autopilot messages" value={dash(fmtNum(totals?.autopilotMessages))} note="Sent by Besty" />
      <Stat label="Offers sent" value={dash(fmtNum(totals?.offersSent))} note="All upsell types" />
      <Stat
        label="Avg response time"
        value={totals ? fmtMinutes(totals.avgResponseTimeMinutes) : '-'}
        note="Weighted across locations"
      />
    </div>
  );
}
